import Point from "@arcgis/core/geometry/Point";
import URLSearchParams from "@ungap/url-search-params";
import {
  LogisticsClusterIcon,
  LogisticsClusterSmallIcon,
  WFPIcon,
} from "../components/Icons/Header-Icons";
import bngrc from "../components/Icons/img/bngrc.jpg";
import Comment from "../components/Report/new/Comment/Comment";
import Input from "../components/Report/new/Input/Input";
import configFile from "../data/config.json";
import { basemapZooms } from "../data/constants";
import { countryCodes } from "../data/countryCodes";
import { allCountryCodes } from "../data/countryISO3Codes";
import symbologies from "../data/symbologies.json";
import { timezones } from "../data/timezones";
import { setActiveModule } from "../redux/action/ActiveModule-actions";
import {
  incrementMapReloadCount,
  setShowModules,
} from "../redux/action/MapView-action";
import { setUserRole } from "../redux/action/userReducer-action";
import {
  setActiveModule_actionType,
  setFilters_actionType,
} from "../redux/constants";
import store from "../redux/store";
import { currValidationFieldName, initialExtent, view } from "./API";
import { getLayerSymbology } from "./symbologies";
import { borderColor } from "./Theme";

const _ = require("lodash");

// Operation related configuration options:
// webmap, title, subtitle, basemap, modules, activeModules, defaultModule,	unFilteredLayers, filteredLayers,
// center, country, zoom, iso3, printServiceUrl, mapTitle, mapAuthor, mapCpr, ops, bsColor, opsColor, lang, sens,
// headerZoomButtonOptions, surveyUrl, surveyHideDetails, surveyISOField, interactive, widgets

export const getConfig = (t, openSnackbar) => {
  try {
    const config = fetchConfigOptions(t, openSnackbar);
    if (config && config.title)
      document.title = `${
        config.role === ROLE_EDITOR
          ? `${t("screen.widget.Editor.name", "Editor")}: `
          : ""
      }${t("ops." + config.id + ".title", config.title)}`;

    return config;
  } catch (err) {
    console.warn(err);
  }
};

export const ROLE_EDITOR = "editor";

export const EMBED_VARIANTS = {
  WEBSITE: "lc-website",
  APP: "lc-app",
};

const fetchConfigOptions = (t, openSnackbar) => {
  const searchParamsInstance = new URLSearchParams(
    window.location.search.toLowerCase()
  );

  const ops = configFile.ops;

  //Read the configuration id from the url parameter 'op'
  let config = null;

  let opParam = searchParamsInstance.get("op");
  if (opParam) {
    ops.forEach((op) => {
      const isMultiAlias =
        Array.isArray(op.alias) && op.alias.includes(opParam);
      if (
        op.id.toLowerCase() === opParam ||
        (typeof op.alias === "string" && op.alias.toLowerCase() === opParam) ||
        isMultiAlias
      ) {
        if (isMultiAlias) {
          config = {
            ...op,
            alias: opParam,
          };
        } else {
          config = op;
        }
      }
    });
    if (!config) {
      openSnackbar(t("screen.message.pageNotFound", { 1: opParam }), 45000);
    } else if (config.opStatus === "inactive") {
      config = null;
      openSnackbar(t("screen.message.inactiveOp", { 1: opParam }), 15000);
    } else if (config.opStatus === "phasingout") {
      openSnackbar(t("screen.message.phasingOutOp"), 15000);
    } else if (config.opStatus === "planning") {
      config = null;
    } else {
      config.opStatus = "active";
    }
  }

  if (!config) config = ops[0];
  const paramRole = searchParamsInstance.get("role");
  store.dispatch(setUserRole(paramRole));

  if (paramRole && paramRole !== ROLE_EDITOR) {
    openSnackbar(t("screen.message.urlError", { 1: paramRole }), 15000);
    config.role = null;
    store.dispatch(setUserRole(null));
  } else {
    const viewerRole = store.getState().mapViewReducer.role;
    if (viewerRole) {
      config.role = viewerRole === ROLE_EDITOR ? viewerRole : null;
      if (!config.role) {
        // Remove role from searchParamsInstance if viewerRole is not editor
        searchParamsInstance.delete("role");
      }
    } else {
      config.role = paramRole;
    }
  }

  if (
    config.embed &&
    config.embed !== EMBED_VARIANTS.APP &&
    config.embed !== EMBED_VARIANTS.WEBSITE
  ) {
    openSnackbar(t("screen.message.urlError", { 1: config.embed }), 15000);
    config.embed = null;
  }

  config.allowOverride =
    configFile.allowURLParamOverride !== undefined
      ? configFile.allowURLParamOverride
      : false;
  config.opDefaults = configFile.opDefaults;
  config.layerConfig = extend(
    configFile.layerDefinition,
    config.layerDefinition
  );
  config.reportDefinition = extend(
    configFile.reportDefinition,
    config.reportDefinition
  );
  config.layerDefaults = configFile.layerDefaults;
  config.widgetGroups = configFile.widgetGroups;
  config.layerSymbologies = symbologies;

  //extending opDefaults for print widget;
  config.printMap = extend(
    configFile.opDefaults.printMap || {},
    config.printMap || {}
  );

  Object.keys(config.layerConfig).forEach((layerAlias) => {
    if (config.layerConfig[layerAlias].extends) {
      const layerToExtend = config.layerConfig[layerAlias].extends;
      const layerConfigToExtend = config.layerConfig[layerToExtend];
      if (layerConfigToExtend) {
        const layerConfigToExtendsWithDefaults = extend(
          configFile.layerDefaults,
          layerConfigToExtend
        );
        config.layerConfig[layerAlias] = extend(
          layerConfigToExtendsWithDefaults,
          config.layerConfig[layerAlias]
        );
      } else
        config.layerConfig[layerAlias] = extend(
          configFile.layerDefaults,
          config.layerConfig[layerAlias]
        );
    } else {
      config.layerConfig[layerAlias] = extend(
        configFile.layerDefaults,
        config.layerConfig[layerAlias]
      );
    }
  });

  if (config.role === ROLE_EDITOR) {
    Object.keys(config.layerConfig).forEach((layerAlias) => {
      if (config.layerConfig[layerAlias].editable) {
        config.layerConfig[layerAlias].viewer =
          config.layerConfig[layerAlias].id;
        config.layerConfig[layerAlias].id =
          config.layerConfig[layerAlias].editable;
      }
    });
  }

  if (config.id && Array.isArray(config.activeModules)) {
    const { operationPreferences } = getOperationPreferences(config.id);

    if (
      !config.activeModules.includes(config.defaultModule) &&
      config.activeModules.length > 0
    ) {
      config.defaultModule = config.activeModules[0];
    }

    if (
      operationPreferences.activeModule &&
      config.activeModules.includes(operationPreferences.activeModule)
    ) {
      config.defaultModule = operationPreferences.activeModule;
    }
    try {
      const isMobileApp = store.getState().mobileReducer.isMobileApp;
      if (isMobileApp) {
        const languageConfig = localStorage.getItem("languageConfig");
        if (languageConfig) {
          const { languageModule } = JSON.parse(languageConfig);
          if (languageModule) {
            config.defaultModule = languageModule;
          }
        }
      }

      const loginModule = localStorage.getItem("loginModule");
      if (loginModule) {
        localStorage.removeItem("loginModule");
        config.defaultModule = loginModule;
        store.dispatch(setShowModules(false));
      }
    } catch (error) {
      console.error("Error fetching state:", error);
    }
  }

  return compileConfigOptions(searchParamsInstance, config, t, openSnackbar);
};

export const getDisclaimerText = (config, activeModule, t) => {
  if (!config || !activeModule || typeof t !== "function") {
    return "";
  }

  const operationDisclaimer = t(
    "ops." + (config.id || "") + ".disclaimer",
    t("screen.message.disclaimer", config.disclaimer || "")
  );
  const configModule = config.modules ? config.modules[activeModule] : null;
  const configModuleDisclaimer =
    configModule?.disclaimer ??
    config.opDefaults?.modules?.[activeModule]?.disclaimer;
  const moduleDisclaimer = t(
    "screen.widget.ModuleSwitcher." + activeModule + ".disclaimer",
    configModuleDisclaimer
  );

  return moduleDisclaimer || operationDisclaimer || "";
};

/**
 * Merge two objects. Similar to jQuery's extend function
 */
export const extend = (a, b) => {
  let ret = {};

  if (typeof a === "object")
    Object.keys(a).forEach((key) => {
      if (a.hasOwnProperty(key)) ret[key] = a[key];
    });

  if (typeof b === "object")
    Object.keys(b).forEach((key) => {
      if (b.hasOwnProperty(key)) {
        const aVal = a[key];
        const bVal = b[key];
        if (isObject(aVal) && isObject(bVal)) ret[key] = extend(aVal, bVal);
        else ret[key] = bVal;
      }
    });

  return ret;
};

/**
 * Is Object? return false for arrays
 */
const isObject = (val) => {
  return typeof val === "object" && !Array.isArray(val);
};
const compileConfigOptions = (
  searchParamsInstance,
  config,
  t,
  openSnackbar
) => {
  if (!config) return null;

  let settings = { ...config.opDefaults, ...config };
  settings.modules = { ...config.opDefaults.modules, ...config.modules };

  if (config.allowOverride) {
    //let doForward = false
    searchParamsInstance.forEach((currVal, currName) => {
      const key = currName.toLowerCase(),
        val = currVal.toLowerCase();
      //if (key !== "op" && key !== "role")
      //	doForward = true

      const urlParam = getUrlParam(key, val, t, openSnackbar);
      if (urlParam) settings[key] = urlParam;
    });

    settings.activeModules = settings.activeModules?.filter((moduleName) => {
      const module = settings.modules[moduleName];
      return (
        (!module.onlyForEditors || config.role === ROLE_EDITOR) &&
        Array.isArray(module.layers) &&
        module.layers.length > 0
      );
    });

    /** Module url parameter will overwrite the defaultModule config option */
    if (settings["module"]) {
      const activeModule = settings["module"].toUpperCase();
      if (
        settings.activeModules &&
        settings.activeModules.includes(activeModule)
      )
        settings.defaultModule = activeModule;
    }
    /*
		if (doForward){
			const suffix = (settings.op ? "op=" + settings.op : "") +
				(settings.role ? "&role=" + settings.role : "")
			//window.history.replaceState({}, document.title, window.location.origin + "/?" + suffix)
		}
*/
  }

  if (
    !settings.defaultModule &&
    settings.activeModules &&
    settings.activeModules.length > 0
  ) {
    settings.defaultModule = settings.activeModules[0];
  }

  /**
   * Filter customization: each module can define its set of filters
   */

  let filters = {};
  settings.activeModules &&
    settings.activeModules.forEach((moduleName) => {
      const moduleFilters = settings.modules[moduleName]?.filterFields;
      const layerFilters = [];
      if (!moduleFilters && settings.modules[moduleName]?.layers) {
        settings.modules[moduleName]?.layers.forEach((layer) => {
          if (Array.isArray(settings.layerConfig[layer]?.filterFields)) {
            layerFilters.push(...settings.layerConfig[layer].filterFields);
          }
        });

        if (settings.modules[moduleName]?.optionalLayers) {
          settings.modules[moduleName]?.optionalLayers.forEach((layer) => {
            if (Array.isArray(settings.layerConfig[layer]?.filterFields)) {
              layerFilters.push(...settings.layerConfig[layer].filterFields);
            }
          });
        }
      }

      const filterDefinition = moduleFilters
        ? moduleFilters
        : settings.filterFields;
      if (filterDefinition)
        filters[moduleName] = getFilterObject(
          [...filterDefinition, ...layerFilters],
          config
        );
    });

  if (settings.filterFields) {
    filters[""] = getFilterObject(settings.filterFields, config);
  }

  settings.defaultFilters = filters;

  let hasStoreFilters = false;
  try {
    const filters = store.getState().filters || {};

    hasStoreFilters = Object.keys(filters).length > 0;
  } catch (err) {
    console.log(err);
  }

  if (!hasStoreFilters) {
    store.dispatch({ type: setFilters_actionType, payload: filters });
  }

  const defaultModule = settings.defaultModule
    ? settings.defaultModule
    : settings.modules[0]
    ? settings.modules[0]
    : "";

  store.dispatch({
    type: setActiveModule_actionType,
    payload: defaultModule,
  });

  const wg = settings.widgetGroups;
  settings.widgets = replaceWidgetGroups(settings.widgets, wg);
  settings.widgetsForEditors = replaceWidgetGroups(
    settings.widgetsForEditors,
    wg
  );
  settings.widgetsForSmallScreen = replaceWidgetGroups(
    settings.widgetsForSmallScreen,
    wg
  );
  settings.widgetsForEmbed = replaceWidgetGroups(settings.widgetsForEmbed, wg);
  settings.widgetsForAppEmbed = replaceWidgetGroups(
    settings.widgetsForAppEmbed,
    wg
  );
  return settings;
};

const getFilterObject = (filterFields, config) => {
  const filterObject = {};
  filterFields.forEach((filterField) => {
    if (!filterField.onlyForEditors || config.role === ROLE_EDITOR)
      filterObject[filterField.name] = filterField.defaultValue
        ? filterField.defaultValue
        : [];
  });
  return filterObject;
};
/**
 * Widget arrays might contain widget group references. these references will be replaced by
 * the widgets from the group definition
 */
const replaceWidgetGroups = (widgetArray, widgetGroups) => {
  let newWidgets = [];
  widgetArray &&
    widgetArray.forEach((widget) => {
      if (widgetGroups && widgetGroups[widget])
        newWidgets.push(...widgetGroups[widget]);
      else newWidgets.push(widget);
    });
  return newWidgets;
};

export const translateConfig = (config, t) => {
  if (!config) return null;

  //Translations
  const translationOpPrefix = "ops." + config.id + ".";
  config.title = t(translationOpPrefix + "title", config.title);
  config.subtitle = t(translationOpPrefix + "subtitle", config.subtitle);
  config.mapTitle = t(translationOpPrefix + "mapTitle", config.mapTitle);

  //Translate zoom buttons
  if (config.headerZoomButtonOptions)
    config.headerZoomButtonOptions.forEach((zoomButton, index) => {
      config.headerZoomButtonOptions[index].name = t(
        translationOpPrefix + "headerZoomButtonOptions." + index + ".name",
        config.headerZoomButtonOptions[index].name
      );
    });

  return config;
};

export const getUrlParam = (name, val, t, openSnackbar) => {
  switch (name) {
    case "country":
      if (allCountryCodes.includes(val.toUpperCase())) return val;

      openSnackbar(t("screen.message.urlError", { 1: name }), 15000);
      return;

    case "center":
      let centerParam = val.split(",").map((coord) => parseFloat(coord));
      if (centerParam.includes(NaN)) {
        openSnackbar(t("screen.message.urlError", { 1: name }), 15000);
        return;
      }

      return centerParam;

    case "zoom":
      const zoom = Number(val);
      if (isNaN(zoom)) {
        openSnackbar(t("screen.message.urlError", { 1: name }), 15000);
        return;
      }

      return zoom;

    case "bsColor":
    case "opsColor":
      return "#" + val;

    // Links
    case "oid":
    case "lid":
    case "op":
    case "role":
    // These params appear after the authentication screen redirect:
    case "state":
    case "code":
    case "persist":
    case "module":
    case "datacollection":
      return val;
    case "lang":
      return val;
    case "embed":
      return val;
    case "widget-open":
      return val;
    case "test_mode":
      return val;
    case "ios_devices":
      return val;
    case "android_devices":
      return val;
    default:
      console.log(":" + name + ":");
      openSnackbar(t("screen.message.urlError", { 1: name }), 15000);
      return null;
  }
};

export const getConfigErrors = (config) => {
  let configKeys = Object.keys(config);
  const errors = configKeys.reduce((acc, paramKey) => {
    if (config[paramKey] === "error") {
      acc.push(`Invalid ${paramKey} entered in URL.`);
    }
    return acc;
  }, []);
  return _.uniq(errors);
};

export const formatDate = (config, value) => {
  return getDateString(config, value); //.slice(0,10)
};

export const formatDateTime = (config, value) => {
  let dateString = getDateString(config, value);
  return dateString.slice(0, 10) + " " + dateString.slice(11, 16);
};

const getDateString = (config, value) => {
  try {
    const iso3 = getConfigISO(config);

    let timezone = Array.isArray(iso3) ? timezones[iso3[0]][0] : "UTC";
    const date = new Date(value);
    let language = localStorage.getItem("i18nextLng") || "en";
    if (language === "en") {
      language = "en-GB";
    }

    return Intl.DateTimeFormat(language, {
      timeZone: "UTC",
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);

    // return iso3 && timezones[iso3[0]]
    //   ? new Date(value).toISOString("en-GB", {
    //       timeZone: timezones[iso3[0]][0],
    //     })
    //   : new Date(value).toISOString("en-GB");
  } catch (err) {
    return Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(value);
    // return new Date(value).toISOString("en-GB");
  }
};

export const renderCell = (field, value, t, config) => {
  if (!field) return;

  let cellValue =
    field.type === "date"
      ? formatDate(config, value)
      : field.domain
      ? renderDomain(field, value, t)
      : field.type === "string"
      ? linkify(value)
      : field.type === "small-integer"
      ? t("layer.domain." + field.name + "." + value, `${value}`)
      : field.type === "string"
      ? value.trim()
      : value;

  if (typeof cellValue === "string" && cellValue.trim() === "") {
    cellValue = "";
  } else if (
    !isNaN(Number(cellValue)) &&
    cellValue !== null &&
    cellValue !== ""
  ) {
    cellValue = new Intl.NumberFormat("en-US").format(cellValue);
  }

  return typeof cellValue === "string" ? cellValue.trim() : cellValue;
};

export const renderMobileCell = (f, value, onChangeHandler) => {
  const border = standardizeColor(borderColor);
  switch (f.type) {
    case "double":
      return (
        <Input
          key={"i" + f.name}
          noMargin
          type="number"
          step="any"
          defaultValue={value}
          placeholder="0.00"
          borderColor={border}
          field={f}
          onChange={onChangeHandler}
        />
      );
    case "integer":
      return (
        <Input
          key={"i" + f.name}
          noMargin
          step="1"
          type="number"
          defaultValue={value}
          placeholder="0"
          borderColor={border}
          field={f}
          onChange={onChangeHandler}
        />
      );

    case "date":
      return (
        <Input
          key={"i" + f.name}
          noMargin
          type="date"
          defaultValue={value}
          field={f}
          borderColor={border}
          onChange={onChangeHandler}
        />
      );
    default:
      if (f.length > 100) {
        return (
          <Comment
            key={"i" + f.name}
            noMargin
            rows="4"
            maxLength={f.length}
            height={100}
            defaultValue={value}
            borderColor={border}
            field={f}
            onChange={onChangeHandler}
          />
        );
      } else {
        return (
          <Input
            key={"i" + f.name}
            noMargin
            type="text"
            maxLength={f.length}
            defaultValue={value}
            borderColor={border}
            field={f}
            onChange={onChangeHandler}
          />
        );
      }
  }
};

export const renderCellInEditor = (f, value, onChangeHandler) => {
  if (f.domain) {
    const domain = f.domain;

    return (
      <select
        onChange={onChangeHandler}
        data-field-name={f.name}
        className="esri-input esri-feature-form__input"
        defaultValue={value}
      >
        {f.nullable && <option key={null} value={""}></option>}
        {domain.codedValues.map((cv) => (
          <option key={cv.code} value={cv.code}>
            {cv.name}
          </option>
        ))}
      </select>
    );
  }

  switch (f.type) {
    case "double":
      return (
        <input
          key={"i" + f.name}
          type="number"
          step="any"
          className="esri-input"
          data-field-name={f.name}
          onChange={onChangeHandler}
          defaultValue={value}
          placeholder="0.00"
        />
      );
    case "integer":
      return (
        <input
          key={"i" + f.name}
          type="number"
          step="1"
          className="esri-input"
          data-field-name={f.name}
          onChange={onChangeHandler}
          defaultValue={value}
          placeholder="0"
        />
      );
    case "date":
      return (
        <input
          key={"i" + f.name}
          type="date"
          onChange={onChangeHandler}
          className="esri-input"
          data-field-name={f.name}
          defaultValue={value}
        />
      );
    default:
      if (f.length > 60)
        return (
          <textarea
            key={"i" + f.name}
            rows="4"
            maxLength={f.length}
            data-field-name={f.name}
            onChange={onChangeHandler}
            defaultValue={value}
          />
        );
      else
        return (
          <input
            key={"i" + f.name}
            type="text"
            className="esri-input"
            maxLength={f.length}
            data-field-name={f.name}
            onChange={onChangeHandler}
            defaultValue={value}
          />
        );
  }
};

export const renderDomain = (field, value, t) => {
  if (Array.isArray(field))
    return field
      .map((f, idx) => renderSingleDomain(f, value[idx], t))
      .join(" ");

  return renderSingleDomain(field, value, t);
};

export const renderSingleDomain = (field, value, t) => {
  if (!field || !field.domain || field.domain.length < 1) {
    return t(
      "layer.domain." + field.name + "." + value,
      t("screen.domain.unspecified", "Unspecified")
    );
  }

  const codedValues = field.domain.codedValues;
  if (field.nullable && value === null) {
    return t("screen.domain.unspecified", "Unspecified");
  }
  const codedVal = codedValues.filter((codedVal) => {
    return typeof codedVal.code === "number"
      ? codedVal.code === Number(value)
      : codedVal.code === value;
  })[0];

  return t(
    "layer.domain." + field.name + "." + value,
    codedVal ? codedVal.name : t("screen.domain.unspecified", "Unspecified")
  );
};

export const renderFieldTitle = (field, t) => {
  return t("layer.fieldAlias." + field.name + ".title", field.alias);
};

const linkify = (value) => {
  if (!value) return;

  const urlPattern =
    /\b(?:https?|ftp):\/\/[a-z0-9-+&@#/%?=~_|!:,.;]*[a-z0-9-+&@#/%=~_|]/gim;
  const emailPattern = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gim;

  const linkedText = value.replace(
    urlPattern,
    '<a target="_blank" href="$&" rel="noreferrer" style="word-break: break-all">$&</a>'
  );

  return linkedText.replace(
    emailPattern,
    '<a href="mailto:$&" style="word-break: break-all">$&</a>'
  );
};

export const setAsyncError = (err, setError, EsriClass) => {
  let errorMessage =
    "An internal error occurred. Please check URL and try again.";
  console.warn(`Error${EsriClass ? `: Check ${EsriClass}` : ""}`);
  console.warn(err);
  setError([errorMessage]);
};

// The target point is a new camera obtained by shifting the current camera 30 degrees to the east
export const goToRegion = (center) => {
  let pt = new Point({
    latitude: center.y,
    longitude: center.x,
  });

  view
    .goTo({ target: pt, zoom: center.zoom }, { duration: 2000 })
    .catch(function (error) {
      if (error.name !== "AbortError") console.error(error);
    });
};

export const clickEventOnFeature = async (feature, zoomOptions = {}) => {
  try {
    const {
      zoom = true,
      duration = 2000,
      showPopup = true,
      scale = initialExtent.width / 3,
    } = zoomOptions;

    view.closePopup();

    const options = {
      target: feature.geometry,
    };

    const viewScale = view?.scale || 0;

    if (zoom && viewScale > scale) {
      options.scale = scale;
    }

    let center = feature.geometry;

    if (feature.geometry?.extent && feature.geometry.extent.center) {
      center = feature.geometry.extent.center;
    }

    if (showPopup) {
      view.openPopup({
        location: center,
        features: [feature],
      });
    }

    if (feature.geometry?.type === "polyline" && feature.geometry.extent) {
      options.target = center;
      await view.goTo(options, { duration: duration });
    } else {
      await view.goTo(options, { duration: duration });
    }
  } catch (error) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
  }
};

export const isTouchScreen = () => {
  return (
    "ontouchstart" in window || // html5 browsers
    navigator.maxTouchPoints > 0 || // future IE
    navigator.msMaxTouchPoints > 0
  ); // current IE10
};

export const appendParamToURL = (url, param, val) => {
  return url + (url.includes("?") ? "&" : "?") + param + "=" + val;
};

export const getExcludeFields = (layer) => {
  let excludeFields = [
    layer.objectIdField,
    layer.globalIdField,
    //layer.geometryFieldsInfo?.shapeLengthField,
    layer.geometryFieldsInfo?.shapeAreaField,
    layer.editFieldsInfo?.creationDateField,
    layer.editFieldsInfo?.creatorField,
  ];

  if (
    layer.layerConfig?.customPopupOps?.excludeFields &&
    Array.isArray(layer.layerConfig?.customPopupOps?.excludeFields)
  )
    excludeFields = excludeFields.concat(
      layer.layerConfig.customPopupOps.excludeFields
    );

  return excludeFields;
};

/**
 * Match fields by pattern(s). excluded fields are not included in the result
 */
export const getFieldsByPattern = (
  layer,
  fieldPatterns,
  excludeFields = false
) => {
  if (!fieldPatterns) return [];

  const excludedFields = excludeFields ? getExcludeFields(layer) : [];
  let ret = [];
  fieldPatterns.forEach((pattern) => {
    ret = ret.concat(
      layer.fields
        .filter((field) => !excludedFields.includes(field.name))
        .filter((field) => {
          const fieldMatches = field.name.match("^" + pattern + "$");
          return fieldMatches && fieldMatches.length > 0;
        })
    );
  });

  return ret;
};

export const createSharableURL = (feature, config, activeModule) => {
  let url = window.location.href.split("?")[0] + "?";

  if (config.alias) url += "op=" + config.alias;
  else url += "op=" + config.id;

  if (feature.geometry) {
    if (
      feature.geometry.type === "point" &&
      feature.geometry.latitude &&
      feature.geometry.longitude
    ) {
      url +=
        "&center=" +
        feature.geometry.longitude +
        "," +
        feature.geometry.latitude;
    } else if (feature.geometry.extent) {
      const center = feature.geometry.extent.center;
      if (center?.longitude && center?.latitude) {
        url += "&center=" + center.longitude + "," + center.latitude;
      }
    }
  }

  if (view.zoom) url += "&zoom=" + view.zoom;

  if (activeModule) url += "&module=" + activeModule;

  const layer = feature.sourceLayer || feature.layer;

  const globalIdField = layer.objectIdField;
  url += "&oid=" + feature.attributes[globalIdField];

  if (config.role === ROLE_EDITOR) {
    url += "&lid=" + layer.layerConfig.viewer;
  } else {
    url += "&lid=" + layer.layerConfig.id;
  }

  return url;
};

export const togglePopupEditor = ({ view, expand }) => {
  let isCluster;

  view.map.layers?.map((layer) => {
    if (
      layer.type === "feature" &&
      layer.layerConfig &&
      layer.layerConfig.cluster &&
      layer.layerConfig.cluster.isClustered &&
      layer.visible
    ) {
      isCluster = true;
    }
  });

  if (expand.viewModel.expanded) {
    view.popupEnabled = false;
    view.closePopup();
  } else {
    view.popupEnabled = true;
  }
};

export const getHeaderIcon = (config) => {
  switch (config.header.icon) {
    case "wfp":
      return <WFPIcon />;
    case "bngrc":
      return <img src={bngrc} alt={"Bngrc"} height={50} width={50} />;
    default:
      return (
        <a
          href="https://logcluster.org/en"
          target="_blank"
          style={{ display: "flex" }}
        >
          <LogisticsClusterIcon key="lci" />
          <LogisticsClusterSmallIcon key="lcsi" />
        </a>
      );
  }
};

export const locales = {
  en: "en-GB",
  fr: "fr-FR",
  es: "es-ES",
  tr: "tr-TR",
  ru: "ru-RU",
  ar: "ar-SA",
  zh: "zh-CN",
  pt: "pt-PT",
};

const getRelativeTimeUnit = (unit, languageCode) => {
  const rtf = new Intl.RelativeTimeFormat(languageCode, { numeric: "auto" });
  const parts = rtf.formatToParts(1, unit);
  const unitPart = parts.find((part) => part.type === "unit");
  return unitPart ? unitPart.value : unit;
};

export const timeAgo = (
  timestamp,
  languageCode = "en",
  showTime = true,
  ops = {}
) => {
  const locale = locales[languageCode] ?? languageCode;
  let value;
  const now = new Date();

  const date = typeof timestamp === "number" ? new Date(timestamp) : timestamp;
  const diff = Math.abs(now.getTime() - date.getTime());
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  // const diff = (now.getTime() - date.getTime()) / 1000;
  // const minutes = Math.floor(diff / 60);
  // const hours = Math.floor(minutes / 60);
  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: "short",
  });

  const options = {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
    ...ops,
  };

  if (now.toDateString() !== date.toDateString() && days >= 1) {
    if (showTime) {
      options.hour = "numeric";
      options.minute = "numeric";
    }

    const formattedDate = formatIntlDateTime({
      languageCode,
      options,
      date: date,
    });

    if (!showTime) {
      return `${formattedDate}`;
    }

    return `${formattedDate} (UTC)`;
  }

  if (hours > 0) {
    if (now.getTime() - date.getTime() > 0) {
      value = rtf.format(0 - hours, "hour");
    } else {
      value = rtf.format(hours, "hour");
    }
  } else if (minutes > 0) {
    if (now.getTime() - date.getTime() < 0) {
      value = rtf.format(minutes, "minute");
    } else {
      value = rtf.format(0 - minutes, "minute");
    }
  } else {
    value = "now";
  }

  return value;
};

export const formatIntlDateTime = ({
  date,
  languageCode = "en",
  options = {},
  separator = "/",
}) => {
  if (!date) return "";
  const locale = locales[languageCode] ?? languageCode;
  let formattedDate = Intl.DateTimeFormat(locale, {
    year: options?.year ?? "numeric",
    month: options?.month ?? "numeric",
    day: options?.day ?? "numeric",
    ...options,
  }).format(date);

  if (separator !== "/") {
    formattedDate = formattedDate.split("/").join(separator);
  }
  return formattedDate;
};

let applied = {};

export const applyZoomLevelFilter = (l, config) => {
  const batchEditor = view.ui.find("BatchEditor");
  const editor = view.ui.find("Edtior");

  if (
    (batchEditor && batchEditor.viewModel.expanded) ||
    (editor && editor.viewModel.expanded)
  ) {
    return;
  }

  const sizeMap = getLayerSymbology(l, config)?.sizeMap;

  if (!!sizeMap && sizeMap.zoom && l.visible) {
    view
      .whenLayerView(l)
      .then((lv) => {
        if (sizeMap?.zoom) {
          const zoom = view.zoom;
          const showSymbolsCodeValues = [];
          Object.keys(sizeMap.zoom).forEach((k) => {
            if (zoom >= k) {
              const values = sizeMap.zoom[k];
              showSymbolsCodeValues.push(...values);
            }
          });

          if (
            showSymbolsCodeValues.length > 0 &&
            applied[l.id]?.length !== showSymbolsCodeValues.length
          ) {
            applied[l.id] = showSymbolsCodeValues;
            const where = showSymbolsCodeValues.map(
              (v) => sizeMap.field + (v === null ? " is " : " = ") + v
            );
            lv.filter = {
              where: where.join(" or "),
            };
          }
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }
};

export const removeZoomLevelFilter = (lv) => {
  applied = {};
  lv.filter = undefined;
};

export const getFeatureEditDate = (feature) => {
  const editDateFieldName = feature.layer.editFieldsInfo?.editDateField;
  if (!editDateFieldName) return;

  return feature.attributes[editDateFieldName];
};

export const getFeatureLatestUpdateDate = (feature, fieldName) => {
  const layer = feature.layer;
  if (layer.fields.some((f) => f.name === fieldName)) {
    return feature.attributes[fieldName];
  }

  return feature.attributes[layer?.editFieldsInfo?.editDateField];
};

/**
 * Retrieves the ISO3 configuration for the active module or falls back to the default configuration.
 *
 * @param {Object} config - The configuration object.
 * @param {Object} config.modules - The modules configuration object.
 * @param {string|string[]} config.iso3 - The default ISO3 configuration.
 * @returns {string[]} An array of ISO3 codes.
 */
export const getConfigISO = (config) => {
  let iso3;

  try {
    const activeModule = store.getState().activeModule;
    const moduleConfig = activeModule ? config.modules[activeModule] : {};

    iso3 = moduleConfig?.iso3 || config.iso3;
  } catch (err) {
    console.warn("Error fetching iso3:", err);
    iso3 = config.iso3;
  }

  // Ensure iso3 is always assigned a value
  if (!iso3) {
    iso3 = config.iso3;
  }

  // Return iso3 as an array
  return Array.isArray(iso3) ? iso3 : iso3 ? [iso3] : [];
};

export const getISO3List = (config) => {
  const currIso3 = getConfigISO(config);

  const iso3Options = countryCodes.map((item) => {
    return {
      code: item.ISO3,
      name: `${item.ISO3} (${item.name})`,
    };
  });

  // Sort so currIso3 codes appear first
  return iso3Options.sort((a, b) => {
    const aInCurr = currIso3.includes(a.code) ? 0 : 1;
    const bInCurr = currIso3.includes(b.code) ? 0 : 1;
    return aInCurr - bInCurr;
  });
};

export const observerCallback = (entries, handler) => {
  window.requestAnimationFrame(() => {
    if (!Array.isArray(entries) || !entries.length) {
      return;
    }
    handler(entries);
  });
};

export const createObserver = (handler) => {
  return new ResizeObserver((entries) => observerCallback(entries, handler));
};

export const convertRGBAToHexA = (rgba, forceRemoveAlpha = false) => {
  return (
    "#" +
    rgba
      .replace(/^rgba?\(|\s+|\)$/g, "") // Get's rgba / rgb string values
      .split(",") // splits them at ","
      .filter((string, index) => !forceRemoveAlpha || index !== 3)
      .map((string) => parseFloat(string)) // Converts them to numbers
      .map((number, index) => (index === 3 ? Math.round(number * 255) : number)) // Converts alpha to 255 number
      .map((number) => number.toString(16)) // Converts numbers to hex
      .map((string) => (string.length === 1 ? "0" + string : string)) // Adds 0 when length of one number is 1
      .join("")
  ); // Puts the array to togehter to a string
};

//converts rgba and color names (green, red, etc.) to HEX color code
export const standardizeColor = (str) => {
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (str && str.includes("rgb")) {
      str = convertRGBAToHexA(str);
    }

    ctx.fillStyle = str;
    let result = ctx.fillStyle;

    if (result && result.includes("rgb")) {
      result = convertRGBAToHexA(result);
    }
    return result;
  } catch (err) {
    return "#000";
  }
};

export const urlAndEmailRegex =
  /\b(?:https?|ftp):\/\/[a-z0-9-+&@#/%?=~_|!:,.;]*[a-z0-9-+&@#/%=~_|]|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/gim;

export const parseTextWithLinks = (text, stringify = false) => {
  if (text == null || typeof text !== "string") return [];

  const parts = text.split(urlAndEmailRegex);
  const matches = text.match(urlAndEmailRegex) || [];
  const result = [];

  parts.forEach((part, index) => {
    result.push(part);
    if (matches[index]) {
      if (matches[index].includes("@")) {
        if (stringify) {
          result.push(
            `<a key='email-${index}' href='mailto:${matches[index]}'>${matches[index]}</a>`
          );
        } else {
          result.push(
            <a key={"email-${index}"} href={"mailto:${matches[index]}"}>
              {matches[index]}
            </a>
          );
        }
      } else {
        if (stringify) {
          result.push(
            `<a key='link-${index}' target="_blank" rel="noreferrer" href='${matches[index]}'>${matches[index]}</a>`
          );
        } else {
          result.push(
            <a
              key={`link-${index}`}
              target="_blank"
              rel="noreferrer"
              href={matches[index]}
            >
              {matches[index]}
            </a>
          );
        }
      }
    }
  });

  return result;
};

export const hasUrlPattern = (value) => {
  // const newPattern = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i
  // const urlPattern =
  //   /\b(?:https?|ftp):\/\/[a-z0-9-+&@#/%?=~_|!:,.;]*[a-z0-9-+&@#/%=~_|]/gim;
  return urlAndEmailRegex.test(value);
};

export const compateFeaturesByDate = (a, b, sort = "descending") => {
  const editDateA = getFeatureEditDate(a);
  if (!editDateA) return 1;

  const editDateB = getFeatureEditDate(b);
  if (!editDateB) return 1;

  if (sort === "descending") {
    return editDateA > editDateB ? -1 : 1;
  } else {
    return editDateA < editDateB ? -1 : 1;
  }
};

export const isYesNoDomain = (field, layer) => {
  if (!field?.domain || !Array.isArray(layer?.layerConfig?.toggleFields))
    return false;

  return layer.layerConfig.toggleFields.includes(field.name);

  const check = {
    yes: false,
    no: false,
    unknown: false,
  };

  if (field?.domain) {
    field.domain.codedValues.forEach((cv) => {
      const lowerName = cv.name.toLowerCase();
      if (lowerName in check) {
        check[lowerName] = true;
      }
    });
  }

  return Object.values(check).filter((isTrue) => !isTrue).length === 0;
};

export const getOperationPreferences = (id) => {
  const storedPreferences = localStorage.getItem("savedPreferences");
  let preferences = {};
  if (storedPreferences) {
    preferences = JSON.parse(storedPreferences);
  }

  const operationPreferences = preferences[id] || {};
  return {
    allPreferences: preferences,
    operationPreferences,
  };
};

export const getVisibleFilters = (config, activeModule, filters) => {
  let fields = {};
  let fieldsLayer = {};
  const filterArray = config.filterFields.map((item) => item.name);

  if (!view) return fields;
  view.map.layers.forEach((layer) => {
    layer.fields &&
      layer.visible &&
      filters[activeModule] &&
      Object.keys(filters[activeModule]).forEach((filterField) => {
        layer.fields &&
          layer.fields
            .filter((field) => field.name === filterField)
            .forEach((field) => {
              if (filterArray.includes(field.name)) {
                if (!fieldsLayer[field.name]) {
                  fieldsLayer[field.name] = [];
                }

                fieldsLayer[field.name].push(layer);

                fields[field.name] = {
                  field,
                  layer,
                  allLayers: fieldsLayer[field.name],
                };
              }
            });
      });
  });

  return fields;
};

export const getOpsColor = (config, module = null) => {
  let selectedModule = module;
  if (!selectedModule) {
    const { activeModule } = store.getState();
    selectedModule = activeModule;
  }

  if (
    config?.modules &&
    config.modules[selectedModule] &&
    config.modules[selectedModule].moduleColor
  ) {
    return config.modules[selectedModule].moduleColor;
  }

  return config.opsColor;
};

export const toggleEditorWidget = (expand) => {
  const editorWidget = view.ui.find("Editor");
  if (editorWidget) {
    editorWidget.expanded = expand;
  }
};

const BATCH_SIZE = 10; // Batch size of 10

/**
 * Applies edits to features in batches to avoid server timeouts and errors.
 *
 * @param {Object} layer - The layer to which the edits will be applied.
 * @param {Array} features - The features to be updated.
 * @returns {Object} results - The results of the update operations.
 */
export const applyEditsInBatches = async (layer, features) => {
  const results = { updateFeatureResults: [] };

  const applyBatch = async (batch) => {
    try {
      const res = await layer.applyEdits({ updateFeatures: batch });
      results.updateFeatureResults.push(...(res.updateFeatureResults || []));
    } catch (err) {
      console.error("Error applying batch edits:", err);
    }
  };

  for (let i = 0; i < features.length; i += BATCH_SIZE) {
    const batch = features.slice(i, i + BATCH_SIZE);
    await applyBatch(batch);
  }

  results.updateFeatureResults.map((result) => {
    if (result?.error) {
      throw new Error("Error applying edit: " + result.error.message);
    }
  });

  return results;
};

// Helper function to convert File object to base64
export const convertFileToBase64 = (file) => {
  return new Promise((resolve) => {
    // Check if file is valid
    if (!file || !(file instanceof File)) {
      console.warn("Invalid file object provided to convertFileToBase64");
      resolve(null);
      return;
    }

    // Check file size (limit to 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      console.warn(`File size exceeds 10MB limit: ${file.name}`);
      resolve(null);
      return;
    }

    // Check if file type is supported
    const supportedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];
    if (!supportedTypes.includes(file.type)) {
      console.warn(`Unsupported file type: ${file.type}`);
      // Continue anyway as we'll try to convert it
    }

    // Set up timeout (30 seconds)
    const timeout = setTimeout(() => {
      console.error("File conversion timed out");
      resolve(null);
    }, 30000);

    try {
      const reader = new FileReader();

      reader.onload = () => {
        clearTimeout(timeout);

        try {
          if (!reader.result) {
            console.warn("FileReader returned empty result");
            resolve(null);
            return;
          }

          const resultString = reader.result.toString();
          const commaIndex = resultString.indexOf(",");

          if (commaIndex === -1) {
            console.warn("Invalid data URL format (no comma found)");
            resolve(null);
            return;
          }

          const base64String = resultString.substring(commaIndex + 1);
          resolve(base64String);
        } catch (innerError) {
          console.error("Error processing FileReader result:", innerError);
          resolve(null);
        }
      };

      reader.onerror = (error) => {
        clearTimeout(timeout);
        console.error("FileReader error:", error);
        resolve(null);
      };

      reader.onabort = () => {
        clearTimeout(timeout);
        console.warn("FileReader operation aborted");
        resolve(null);
      };

      // Start reading the file
      reader.readAsDataURL(file);
    } catch (error) {
      clearTimeout(timeout);
      console.error("Error initiating file conversion:", error);
      resolve(null);
    }
  });
};

export const triggerGAEvent = ({ event, params }) => {
  try {
    if (!event || !params) {
      console.warn("Event name or label is missing");
      return;
    }

    if (window.gtag && process.env.NODE_ENV === "production") {
      window.gtag("event", event, {
        ...params,
      });
    }
  } catch (error) {
    console.log(error);
  }
};

export const groupReportsByValidationField = (reports, config, t) => {
  if (!Array.isArray(reports) || reports.length === 0) return [];

  const symbologyLayer = reports[0].sourceLayer || reports[0].layer;
  const symbology = getLayerSymbology(symbologyLayer, config);
  const colorMap = symbology.colorMap?.default || {};

  const reportsMap = {};
  reports.forEach((report) => {
    const value = report.attributes.currvalidationfield;
    if (!colorMap[value]) return;
    if (reportsMap[value]) {
      reportsMap[value].count++;
    } else {
      reportsMap[value] = {
        color: colorMap[value] || symbology.defaultColor,
        count: 1,
        title: t("layer.domain.currvalidationfield." + value, `${value}`),
        code: value,
      };
    }
  });

  return Object.values(reportsMap || {});
};

export const getLayerReports = (layer, reportsLayerMap) => {
  const results = [];
  try {
    if (!layer || !reportsLayerMap) return results;

    const viewerId = layer?.layerConfig?.viewer;
    if (viewerId) {
      const viewerReports = reportsLayerMap[viewerId] || [];
      results.push(...viewerReports);
    }

    const editorId = layer?.layerConfig?.id;
    if (editorId) {
      const editorReports = reportsLayerMap[editorId] || [];
      results.push(...editorReports);
    }
  } catch (err) {
    console.error("Error in getLayerReports:", err);
  }

  return results;
};

export const filterReportTags = (tags) => {
  if (!Array.isArray(tags)) return [];

  return tags.filter((cv) => cv.code !== 2);
};

export const sortReports = (reports, reportLayer, sortType) => {
  if (!Array.isArray(reports) || reports.length === 0) return [];
  const editDateField = reportLayer?.editFieldsInfo?.editDateField;

  const result = [...reports].sort((a, b) => {
    // 1. currValidationFieldName === 1 comes first
    const aValid = a.attributes[currValidationFieldName] === 1 ? 0 : 1;
    const bValid = b.attributes[currValidationFieldName] === 1 ? 0 : 1;
    if (aValid !== bValid) return aValid - bValid;

    // 2. Sort by editDate descending (latest first)
    const aDate = a.attributes[editDateField] || 0;
    const bDate = b.attributes[editDateField] || 0;
    return bDate - aDate;
  });
  return result;
};

export const sortFeaturesByReports = (
  features,
  reportsFeatureMap,
  reportLayer,
  sortFunc
) => {
  if (!Array.isArray(features) || features.length === 0) return [];
  const editDateField = reportLayer?.editFieldsInfo?.editDateField;

  return [...features].sort((a, b) => {
    const aId = a.getObjectId();
    const bId = b.getObjectId();

    const aReports = reportsFeatureMap[aId] || [];
    const bReports = reportsFeatureMap[bId] || [];

    // 1. currValidationFieldName === 1 comes first (if any report has it)
    const aValid = aReports.some(
      (r) => r.attributes[currValidationFieldName] === 1
    )
      ? 0
      : 1;
    const bValid = bReports.some(
      (r) => r.attributes[currValidationFieldName] === 1
    )
      ? 0
      : 1;
    if (aValid !== bValid) return aValid - bValid;

    return sortFunc(a, b);
  });
};

export const isReportsLayer = (layer) => {
  return (
    layer?.layerConfig?.isShownInReportManager &&
    (layer?.layerConfig?.alias === "pacReports" ||
      layer?.layerConfig?.extends === "pacReports")
  );
};

/**
 * Returns the minimum zoom level for a given map view.
 *
 * If the view or its basemap is not properly defined, returns zoom as is.
 * Otherwise, checks the basemap's minimum zoom level and ensures the returned zoom
 * is not less than this value.
 *
 * @param {Object} view - The map view object containing map and basemap information.
 * @param {number} zoom - The requested zoom level.
 * @returns {number} The minimum allowed zoom level for the view.
 */
export const getViewMinZoom = (view, zoom) => {
  try {
    if (
      !view ||
      !view.map ||
      !view.map.basemap ||
      !view.map.basemap.portalItem
    ) {
      return zoom;
    }
    const id = view.map.basemap.portalItem.id;
    return getBasemapMinZoom(id, zoom);
  } catch (err) {
    console.log(err);
  }

  return zoom;
};

export const getBasemapMinZoom = (id, zoom) => {
  try {
    const basemapZoom = basemapZooms[id];

    if (basemapZoom?.minZoom && zoom < basemapZoom.minZoom) {
      return basemapZoom.minZoom;
    }
  } catch (err) {}
  return zoom;
};

export const getBasemapZoom = (id) => {
  try {
    const basemapZoom = basemapZooms[id];

    if (typeof basemapZoom?.zoom === "number") {
      return basemapZoom.zoom;
    }
  } catch (err) {}
  return 0;
};

export const switchConfigRole = async ({
  role,
  setConfig,
  t,
  openSnackbar,
  currModule,
  dispatch,
}) => {
  dispatch(setUserRole(role));
  const conf = await getConfig(t, openSnackbar); // Make getConfig async if needed
  if (
    Array.isArray(conf.activeModules) &&
    conf.activeModules.includes(currModule)
  ) {
    dispatch(setActiveModule(currModule));
    dispatch(setShowModules(false));
  } else {
    dispatch(setShowModules(true));
  }

  setConfig(conf);

  // Replace setTimeout with a Promise for async/await
  await new Promise((resolve) => setTimeout(resolve, 100));

  if (role !== ROLE_EDITOR) {
    const url = new URL(window.location.href);
    url.searchParams.delete("role");
    window.history.replaceState({}, document.title, url.toString());
  }
  dispatch(incrementMapReloadCount());
};
